import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../../components';
import VideoPlayer from '../../components/VideoPlayer';
import { getCourseById, getCourses, CourseDoc, ItemDoc } from '../../services/course-service';
import { getEnrollmentByCourse, getMyEnrollments, markItemComplete, EnrollmentDoc } from '../../services/enrollment-service';
import { getQuizAttempts, QuizAttempt } from '../../services/quiz-attempt-service';
import { usePhases } from '../../contexts/PhaseContext';

function toYouTubeEmbed(url: string): string {
  if (!url) return '';
  try {
    const u = new URL(url);
    let videoId = '';
    if (u.hostname.includes('youtu.be')) {
      videoId = u.pathname.slice(1).split('?')[0];
    } else if (u.hostname.includes('youtube.com')) {
      videoId = u.searchParams.get('v') || '';
    }
    if (!videoId) return '';
    return `https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1`;
  } catch {
    return '';
  }
}

const calcProgress = (c: CourseDoc, en: EnrollmentDoc | null, qa: QuizAttempt[]): number => {
  const items = (c.modules || []).flatMap(m => m.items);
  if (!items.length) return 100;
  if (!en) return 0;
  const passedSet = new Set(qa.filter(a => a.passed).map(a => a.itemId));
  const compSet   = new Set(en.completedItems);
  const done = items.filter(item => {
    const iid = item._id as string;
    return item.type === 'quiz' ? passedSet.has(iid) : compSet.has(iid);
  }).length;
  return Math.round((done / items.length) * 100);
};

const CourseDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentPhase, phases, markPhaseComplete, loading: phasesLoading } = usePhases();

  const [course,        setCourse]       = useState<CourseDoc | null>(null);
  const [enrollment,    setEnrollment]   = useState<EnrollmentDoc | null>(null);
  const [quizAttempts,  setQuizAttempts] = useState<QuizAttempt[]>([]);
  const [selectedItem,  setSelectedItem] = useState<ItemDoc | null>(null);
  const [openModules,   setOpenModules]  = useState<Set<number>>(new Set([0]));
  const [isLoading,     setIsLoading]    = useState(true);
  const [error,         setError]        = useState('');
  const [needsPhaseCheck, setNeedsPhaseCheck] = useState(false);
  const completionTriggered = useRef(false);

  useEffect(() => {
    if (!id) return;
    Promise.all([getCourseById(id), getEnrollmentByCourse(id), getQuizAttempts(id)])
      .then(([c, en, qa]) => {
        setCourse(c);
        setEnrollment(en);
        setQuizAttempts(qa);
        if (c.modules?.[0]?.items?.[0]) setSelectedItem(c.modules[0].items[0]);
        if (searchParams.get('quizPassed') === '1' && !completionTriggered.current) {
          if (calcProgress(c, en, qa) === 100) {
            completionTriggered.current = true;
            setNeedsPhaseCheck(true);
          }
        }
      })
      .catch(() => setError('Failed to load course'))
      .finally(() => setIsLoading(false));
  }, [id]); // eslint-disable-line

  const triggerCourseComplete = async () => {
    try {
      const phase = currentPhase;
      if (!phase?.courseIds?.length) { navigate('/app/lms/courses'); return; }
      const [allEnrollments, allAttempts, allCourses] = await Promise.all([
        getMyEnrollments(), getQuizAttempts(), getCourses(),
      ]);
      const enrollMap = new Map<string, any>();
      allEnrollments.forEach((e: any) => {
        const cid = typeof e.courseId === 'object' ? e.courseId._id : e.courseId;
        enrollMap.set(cid, e);
      });
      const passedSet = new Set(allAttempts.filter((a: any) => a.passed).map((a: any) => a.itemId));
      const itemTypeMap = new Map<string, string>();
      allCourses.forEach((c: any) => (c.modules || []).forEach((m: any) =>
        m.items.forEach((i: any) => { if (i._id) itemTypeMap.set(String(i._id), i.type); })
      ));
      const published = allCourses.filter((c: any) => c.status === 'published');
      const allDone = phase.courseIds.every(cid => {
        const c = published.find((x: any) => x._id === cid);
        const en = enrollMap.get(cid);
        if (!c || !en) return false;
        const items = (c.modules || []).flatMap((m: any) => m.items);
        if (!items.length) return true;
        const done = en.completedItems.filter((iid: string) =>
          itemTypeMap.get(iid) === 'quiz' ? passedSet.has(iid) : true
        ).length;
        return done >= items.length;
      });
      if (allDone) {
        const nextPhase = phases.find((p: any) => (p.order ?? 0) === ((phase as any).order ?? 0) + 1);
        await markPhaseComplete(phase.id);
        navigate('/app/dashboard/founder', { state: { unlockedPhase: nextPhase?.title ?? null } });
      } else {
        navigate('/app/lms/courses');
      }
    } catch {
      navigate('/app/lms/courses');
    }
  };

  useEffect(() => {
    if (!needsPhaseCheck || phasesLoading) return;
    setNeedsPhaseCheck(false);
    triggerCourseComplete();
  }, [needsPhaseCheck, phasesLoading]); // eslint-disable-line

  const handleItemClick = (item: ItemDoc) => {
    setSelectedItem(item);
    if (item.type === 'quiz') return;
    if (!id || !item._id || !enrollment) return;
    markItemComplete(id, item._id as string)
      .then(updated => {
        setEnrollment(updated);
        if (course && !completionTriggered.current) {
          if (calcProgress(course, updated, quizAttempts) === 100) {
            completionTriggered.current = true;
            setNeedsPhaseCheck(true);
          }
        }
      })
      .catch(() => {});
  };

  const toggleModule = (idx: number) => {
    setOpenModules(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const isCompleted = (itemId: string) =>
    enrollment?.completedItems.includes(itemId) ?? false;

  const allItems: ItemDoc[] = (course?.modules || []).flatMap(m => m.items);
  const currentIndex = selectedItem ? allItems.findIndex(i => i._id === selectedItem._id) : -1;
  const prevItem = currentIndex > 0 ? allItems[currentIndex - 1] : null;
  const nextItem = currentIndex >= 0 && currentIndex < allItems.length - 1 ? allItems[currentIndex + 1] : null;

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-8 h-8 border-2 border-chart border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!course) {
    return (
      <div className="section-gap">
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold text-text mb-4">
            {error || 'Course Not Found'}
          </h2>
          <Button variant="primary" onClick={() => navigate('/app/lms/courses')}>Back to Courses</Button>
        </div>
      </div>
    );
  }

  const completedSet = new Set(enrollment?.completedItems ?? []);

  // Per-quiz status: attempts sorted ascending → last write = latest attempt
  const quizStatusMap = new Map<string, { passed: boolean; latestScore: number; count: number }>();
  quizAttempts.forEach(a => {
    const prev = quizStatusMap.get(a.itemId);
    quizStatusMap.set(a.itemId, {
      passed: prev?.passed || a.passed,
      latestScore: a.score,
      count: (prev?.count ?? 0) + 1,
    });
  });

  // Progress: quiz items count as complete only if passed; non-quiz items use completedItems
  const allCourseItems = (course.modules || []).flatMap(m => m.items);
  const totalItems = allCourseItems.length;
  const completedCount = allCourseItems.filter(item => {
    const iid = item._id as string | undefined;
    if (!iid) return false;
    if (item.type === 'quiz') return quizStatusMap.get(iid)?.passed ?? false;
    return completedSet.has(iid);
  }).length;
  const pct = totalItems > 0 ? Math.round((completedCount / totalItems) * 100) : 0;

  const renderContent = (item: ItemDoc) => {
    switch (item.type) {
      case 'text':
        return (
          <div
            className="prose prose-invert max-w-none text-muted leading-relaxed"
            dangerouslySetInnerHTML={{ __html: item.textContent || '<p>No content available.</p>' }}
          />
        );
      case 'video': {
        const fileUrl = (item as any).fileUrl || '';
        const embedUrl = fileUrl ? '' : toYouTubeEmbed((item as any).videoUrl || '');
        if (fileUrl) {
          return (
            <div className="relative w-full rounded-lg overflow-hidden bg-black" style={{ aspectRatio: '16/9' }}>
              <VideoPlayer src={fileUrl} className="w-full h-full" />
            </div>
          );
        }
        if (embedUrl) {
          return (
            <div
              className="relative w-full rounded-lg overflow-hidden bg-black"
              style={{ aspectRatio: '16/9' }}
              onContextMenu={e => e.preventDefault()}
            >
              <iframe
                src={embedUrl}
                className="w-full h-full"
                allowFullScreen
                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                title={item.title}
                referrerPolicy="strict-origin"
              />
            </div>
          );
        }
        return <p className="text-muted">No video available.</p>;
      }
      case 'pdf':
        return item.fileUrl ? (
          <embed src={item.fileUrl} type="application/pdf" className="w-full rounded-lg" style={{ height: '70vh' }} />
        ) : (
          <p className="text-muted">No PDF file available.</p>
        );
      case 'quiz': {
        const moduleId = (course?.modules || []).find(m => m.items.some(i => i._id === item._id))?._id;
        const quizStatus = item._id ? quizStatusMap.get(item._id as string) : undefined;
        const btnLabel = quizStatus ? 'Retake Quiz' : 'Generate Quiz';
        return (
          <div className="border border-line rounded-xl p-6">
            <h3 className="text-text font-semibold mb-2">{item.title}</h3>
            <p className="text-muted text-sm mb-4">Test your knowledge with this quiz.</p>
            <Button variant="primary" onClick={() => navigate(`/app/lms/quiz/${item._id}?courseId=${id}&moduleId=${moduleId}`)}>
              {btnLabel}
            </Button>
          </div>
        );
      }
      default:
        return <p className="text-muted">Unsupported content type.</p>;
    }
  };

  return (
    <div className="section-gap">
      {/* Header */}
      <div className="border-b border-line pb-6">
        <div className="flex items-center gap-3 flex-1 min-w-0 mb-4">
          <button onClick={() => navigate('/app/lms/courses')} className="text-muted hover:text-text flex-shrink-0">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </button>
          <h1 className="text-2xl font-bold text-text truncate">{course.title}</h1>
        </div>
        <div className="flex justify-between text-xs text-muted mb-1">
          <span>{completedCount} / {totalItems} items completed</span>
          <span>{pct}%</span>
        </div>
        <div className="w-full bg-surface rounded-full h-1.5">
          <div className="bg-chart h-1.5 rounded-full transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>

      {/* Main: Sidebar + Content */}
      <div className="flex gap-6 min-h-0">
        {/* Left sidebar — module tree */}
        <div className="w-72 flex-shrink-0">
          <div className="sticky top-6 space-y-2">
            {(course.modules || []).map((mod, modIdx) => (
              <div key={mod._id || modIdx} className="border border-line rounded-xl overflow-hidden">
                <button
                  onClick={() => toggleModule(modIdx)}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-surface/30 transition-colors"
                >
                  <span className="text-text font-medium text-sm">{mod.title}</span>
                  <svg
                    className={`w-4 h-4 text-muted transition-transform ${openModules.has(modIdx) ? 'rotate-180' : ''}`}
                    fill="currentColor" viewBox="0 0 20 20"
                  >
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
                {openModules.has(modIdx) && (
                  <div className="border-t border-line">
                    {mod.items.map((item, itemIdx) => {
                      const itemId = item._id as string | undefined;
                      const quizStatus = item.type === 'quiz' && itemId ? quizStatusMap.get(itemId) : undefined;
                      const done   = item.type === 'quiz'
                        ? (quizStatus?.passed ?? completedSet.has(itemId ?? ''))
                        : (itemId ? isCompleted(itemId) : false);
                      const failed = !done && !!quizStatus;
                      const active = selectedItem?._id === item._id;
                      return (
                        <button
                          key={item._id || itemIdx}
                          onClick={() => handleItemClick(item)}
                          className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm transition-colors ${
                            active ? 'bg-chart/10 text-chart' : 'hover:bg-surface/30 text-muted hover:text-text'
                          }`}
                        >
                          {done ? (
                            <div className="w-4 h-4 rounded-full bg-success border-success border flex-shrink-0 flex items-center justify-center">
                              <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </div>
                          ) : failed ? (
                            <div className="w-4 h-4 rounded-full bg-danger border-danger border flex-shrink-0 flex items-center justify-center">
                              <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                              </svg>
                            </div>
                          ) : (
                            <div className="w-4 h-4 rounded-full border border-line flex-shrink-0" />
                          )}
                          <span className="truncate">{item.title}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Right content area */}
        <div className="flex-1 min-w-0 flex flex-col gap-4">
          {selectedItem ? (
            <div className="border border-line rounded-xl p-6">
              <h2 className="text-xl font-semibold text-text mb-6">{selectedItem.title}</h2>
              {renderContent(selectedItem)}
            </div>
          ) : (
            <div className="border border-line rounded-xl p-6">
              <p className="text-muted">Select an item from the sidebar to begin.</p>
            </div>
          )}

          {/* Prev / Next navigation */}
          {selectedItem && (
            <div className="flex items-center justify-between pt-2">
              <Button
                variant="ghost"
                size="sm"
                disabled={!prevItem}
                onClick={() => prevItem && handleItemClick(prevItem)}
              >
                ← Previous
              </Button>
              <span className="text-xs text-muted">
                {currentIndex + 1} / {allItems.length}
              </span>
              <Button
                variant={nextItem ? 'primary' : 'ghost'}
                size="sm"
                disabled={!nextItem}
                onClick={() => {
                  if (!nextItem) return;
                  const modIdx = (course?.modules || []).findIndex(m =>
                    m.items.some(i => i._id === nextItem._id)
                  );
                  if (modIdx >= 0) setOpenModules(prev => new Set(prev).add(modIdx));
                  handleItemClick(nextItem);
                }}
              >
                Next →
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CourseDetail;
